import { FixedViewSchema, TaskField, TaskViewSchema } from '../models/task-config.model';

export interface SimpleViewFilter {
  fieldName: string;
  expression: string;
  operand: string;
}

export interface ViewSchemaGuiSlice {
  fields: TaskField[];
  where: string;
  filters: SimpleViewFilter[];
  groupBy: string;
  orderBy: string;
  skip: number | null;
  take: number | null;
  advancedJson: string;
}

export interface FixedViewFieldGuiRow {
  kind: 'field' | 'formula';
  fieldName: string;
  formula: string;
}

export interface FixedViewSchemaGuiSlice {
  fields: FixedViewFieldGuiRow[];
  advancedJson: string;
}

const INDEX_APP_KNOWN_KEYS = new Set(['fields', 'where', 'filters', 'groupBy', 'orderBy', 'skip', 'take']);
const FIXED_KNOWN_KEYS = new Set(['fields']);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringifyPretty(value: unknown): string {
  return JSON.stringify(value, null, 2);
}

function parseFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

function splitKnownAndAdvanced(
  source: Record<string, unknown>,
  knownKeys: Set<string>,
): { known: Record<string, unknown>; advanced: Record<string, unknown> } {
  const known: Record<string, unknown> = {};
  const advanced: Record<string, unknown> = {};

  Object.entries(source).forEach(([key, value]) => {
    if (knownKeys.has(key)) {
      known[key] = value;
    } else {
      advanced[key] = value;
    }
  });

  return { known, advanced };
}

export function safeParseJsonObject(jsonText: string | null | undefined): Record<string, unknown> | null {
  const text = typeof jsonText === 'string' ? jsonText.trim() : '';
  if (!text) return null;
  const parsed = JSON.parse(text);
  if (!isRecord(parsed)) {
    throw new Error('JSON object required');
  }
  return parsed;
}

function normalizeTaskFields(value: unknown): TaskField[] {
  if (!Array.isArray(value)) return [];

  return value.flatMap((item) => {
    const fieldName = isRecord(item) && typeof item['fieldName'] === 'string' ? item['fieldName'].trim() : '';
    if (!fieldName) {
      return [];
    }

    const field: TaskField = { fieldName };
    if (isRecord(item) && typeof item['formula'] === 'string' && item['formula'].trim()) {
      field.formula = item['formula'].trim();
    }
    if (isRecord(item) && typeof item['value'] === 'string' && item['value'].trim()) {
      field.value = item['value'];
    } else if (isRecord(item) && item['value'] !== undefined && item['value'] !== null) {
      field.value = String(item['value']);
    }
    return [field];
  });
}

function normalizeSimpleFilters(value: unknown): { filters: SimpleViewFilter[]; unsupported: unknown[] } {
  if (!Array.isArray(value)) return { filters: [], unsupported: [] };

  const filters: SimpleViewFilter[] = [];
  const unsupported: unknown[] = [];

  value.forEach((item) => {
    const fieldName = isRecord(item) && typeof item['fieldName'] === 'string' ? item['fieldName'] : null;
    const expression = isRecord(item) && typeof item['expression'] === 'string' ? item['expression'] : null;
    if (
      isRecord(item) &&
      fieldName !== null &&
      expression !== null &&
      !Array.isArray(item['children'])
    ) {
      filters.push({
        fieldName,
        expression,
        operand: item['operand'] == null ? '' : String(item['operand']),
      });
      return;
    }

    unsupported.push(item);
  });

  return { filters, unsupported };
}

export function parseIndexAppViewSchema(value: unknown): ViewSchemaGuiSlice {
  if (!isRecord(value)) {
    return {
      fields: [],
      where: '',
      filters: [],
      groupBy: '',
      orderBy: '',
      skip: null,
      take: null,
      advancedJson: '',
    };
  }

  const { known, advanced } = splitKnownAndAdvanced(value, INDEX_APP_KNOWN_KEYS);
  const { filters, unsupported } = normalizeSimpleFilters(known['filters']);
  if (unsupported.length > 0) {
    advanced['filters'] = known['filters'];
  }

  return {
    fields: normalizeTaskFields(known['fields']),
    where: typeof known['where'] === 'string' ? known['where'] : '',
    filters,
    groupBy: typeof known['groupBy'] === 'string' ? known['groupBy'] : '',
    orderBy: typeof known['orderBy'] === 'string' ? known['orderBy'] : '',
    skip: parseFiniteNumber(known['skip']),
    take: parseFiniteNumber(known['take']),
    advancedJson: Object.keys(advanced).length > 0 ? stringifyPretty(advanced) : '',
  };
}

export function buildIndexAppViewSchema(
  slice: Omit<ViewSchemaGuiSlice, 'advancedJson'>,
  advanced: Record<string, unknown> | null = null,
): TaskViewSchema | null {
  const schema: Record<string, unknown> = advanced ? { ...advanced } : {};

  const fields = slice.fields
    .map((field) => {
      const normalized: TaskField = { fieldName: field.fieldName.trim() };
      if (field.formula?.trim()) normalized.formula = field.formula.trim();
      if (field.value?.trim()) normalized.value = field.value.trim();
      return normalized.fieldName ? normalized : null;
    })
    .filter((field): field is TaskField => field !== null);

  if (fields.length > 0) schema['fields'] = fields;
  if (slice.where.trim()) schema['where'] = slice.where.trim();

  const filters = slice.filters
    .map((filter) => ({
      fieldName: filter.fieldName.trim(),
      expression: filter.expression.trim(),
      operand: filter.operand.trim(),
    }))
    .filter((filter) => filter.fieldName && filter.expression);

  if (filters.length > 0) schema['filters'] = filters;
  if (slice.groupBy.trim()) schema['groupBy'] = slice.groupBy.trim();
  if (slice.orderBy.trim()) schema['orderBy'] = slice.orderBy.trim();
  if (slice.skip != null && Number.isFinite(slice.skip)) schema['skip'] = slice.skip;
  if (slice.take != null && Number.isFinite(slice.take)) schema['take'] = slice.take;

  return Object.keys(schema).length > 0 ? (schema as TaskViewSchema) : null;
}

export function parseFixedViewSchema(value: unknown): FixedViewSchemaGuiSlice {
  if (!isRecord(value)) {
    return { fields: [], advancedJson: '' };
  }

  const { known, advanced } = splitKnownAndAdvanced(value, FIXED_KNOWN_KEYS);
  const rawFields = known['fields'];
  const fields: FixedViewFieldGuiRow[] = Array.isArray(rawFields)
    ? rawFields.flatMap((item): FixedViewFieldGuiRow[] => {
        if (typeof item === 'string' && item.trim()) {
          return [{ kind: 'field' as const, fieldName: item.trim(), formula: '' }];
        }
        const fieldName = isRecord(item) && typeof item['fieldName'] === 'string' ? item['fieldName'].trim() : '';
        const formula = isRecord(item) && typeof item['formula'] === 'string' ? item['formula'].trim() : '';
        if (
          isRecord(item) &&
          fieldName &&
          formula
        ) {
          return [
            {
              kind: 'formula' as const,
              fieldName,
              formula,
            },
          ];
        }
        return [];
      })
    : [];

  return {
    fields,
    advancedJson: Object.keys(advanced).length > 0 ? stringifyPretty(advanced) : '',
  };
}

export function buildFixedViewSchema(
  slice: Omit<FixedViewSchemaGuiSlice, 'advancedJson'>,
  advanced: Record<string, unknown> | null = null,
): FixedViewSchema | Record<string, unknown> | null {
  const schema: Record<string, unknown> = advanced ? { ...advanced } : {};

  const fields: Array<string | { fieldName: string; formula: string }> = [];

  slice.fields.forEach((field) => {
    const fieldName = field.fieldName.trim();
    if (!fieldName) return;

    if (field.kind === 'formula') {
      const formula = field.formula.trim();
      if (!formula) return;
      fields.push({ fieldName, formula });
      return;
    }

    fields.push(fieldName);
  });

  if (fields.length > 0) schema['fields'] = fields;
  return Object.keys(schema).length > 0 ? schema : null;
}
